import axios, { AxiosResponse } from 'axios';
import { User, LoginRequest, LoginResponse, ApiResponse } from '../../../shared/types';
import { getAxiosBaseURL } from '../config/apiConfig';

// Set axios baseURL globally for auth flows (without /api/v1)
const API_BASE_URL = getAxiosBaseURL();
axios.defaults.baseURL = API_BASE_URL;

// Auth endpoints
const AUTH_ENDPOINTS = {
  LOGIN: '/api/v1/auth/login',
  LOGOUT: '/api/v1/auth/logout',
  ME: '/api/v1/auth/me',
  CHANGE_PASSWORD: '/api/v1/auth/change-password',
  VERIFY_TOKEN: '/api/v1/auth/verify-token',
  REFRESH_TOKEN: '/api/v1/auth/refresh-token'
};

// Token storage keys
const TOKEN_KEY = 'quanly_truonghoc_token';
const USER_KEY = 'quanly_truonghoc_user';
const ROLE_PERMISSIONS_KEY = 'quanly_truonghoc_permissions';
const USER_PERMISSIONS_KEY = 'quanly_truonghoc_user_permissions';

export class AuthService {
  // Get stored token
  static getToken(): string | null {
    try {
      // Try localStorage first
      let token = localStorage.getItem(TOKEN_KEY);

      // Fallback to sessionStorage if localStorage failed
      if (!token) {
        token = sessionStorage.getItem(TOKEN_KEY);
        if (token) {
          console.log('🔄 Token retrieved from sessionStorage fallback');
        }
      }

      // Validate token format
      if (token && (token === 'null' || token === 'undefined' || token.length < 10)) {
        console.log('⚠️ Invalid token format detected, clearing...');
        this.clearAuthData();
        return null;
      }

      return token;
    } catch (error) {
      console.error('❌ Error getting token:', error);
      return null;
    }
  }

  // Get stored user
  static getUser(): User | null {
    try {
      // Try localStorage first
      let userJson = localStorage.getItem(USER_KEY);

      // Fallback to sessionStorage if localStorage failed
      if (!userJson) {
        userJson = sessionStorage.getItem(USER_KEY);
        if (userJson) {
          console.log('🔄 User retrieved from sessionStorage fallback');
        }
      }

      if (!userJson || userJson === 'null' || userJson === 'undefined') {
        return null;
      }

      return JSON.parse(userJson);
    } catch (error) {
      console.error('❌ Error getting user:', error);
      return null;
    }
  }

  // Store token, user and permissions with robust error handling
  static setAuthData(token: string, user: User, rolePermissions?: any[], userPermissions?: any[]): void {
    console.log('🔐 setAuthData called with:', {
      token: token ? `${token.substring(0, 20)}...` : 'NULL',
      user: user ? user.Username : 'NULL',
      rolePermissions: rolePermissions?.length || 0,
      userPermissions: userPermissions?.length || 0
    });

    try {
      // Prepare data for storage
      const userJson = JSON.stringify(user);
      const rolePermJson = rolePermissions ? JSON.stringify(rolePermissions) : null;
      const userPermJson = userPermissions ? JSON.stringify(userPermissions) : null;

      // Primary attempt: localStorage
      try {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, userJson);

        if (rolePermJson) {
          localStorage.setItem(ROLE_PERMISSIONS_KEY, rolePermJson);
        }
        if (userPermJson) {
          localStorage.setItem(USER_PERMISSIONS_KEY, userPermJson);
        }

        console.log('✅ Data saved to localStorage');
      } catch (localError) {
        console.warn('⚠️ localStorage failed, trying sessionStorage:', localError);

        // Fallback to sessionStorage
        sessionStorage.setItem(TOKEN_KEY, token);
        sessionStorage.setItem(USER_KEY, userJson);

        if (rolePermJson) {
          sessionStorage.setItem(ROLE_PERMISSIONS_KEY, rolePermJson);
        }
        if (userPermJson) {
          sessionStorage.setItem(USER_PERMISSIONS_KEY, userPermJson);
        }

        console.log('✅ Data saved to sessionStorage (fallback)');
      }

      console.log('✅ Auth data saved successfully');

      // Trigger storage event to update other tabs
      window.dispatchEvent(new Event('storage'));

    } catch (error) {
      console.error('❌ Critical error saving auth data:', error);
      alert(`Không thể lưu thông tin đăng nhập: ${error instanceof Error ? error.message : 'Lỗi không xác định'}`);
    }
  }

  // Clear auth data
  static clearAuthData(): void {
    try {
      // Clear from localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(ROLE_PERMISSIONS_KEY);
      localStorage.removeItem(USER_PERMISSIONS_KEY);

      // Clear from sessionStorage (fallback)
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
      sessionStorage.removeItem(ROLE_PERMISSIONS_KEY);
      sessionStorage.removeItem(USER_PERMISSIONS_KEY);

      // Also clear old keys for backward compatibility
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('permissions');
      localStorage.removeItem('userPermissions');

      sessionStorage.removeItem('token');
      sessionStorage.removeItem('userInfo');
      sessionStorage.removeItem('permissions');
      sessionStorage.removeItem('userPermissions');

      console.log('🧹 All auth data cleared from both localStorage and sessionStorage');

      // Trigger storage event to update other tabs
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error('❌ Error clearing auth data:', error);
    }
  }

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  // 🔒 SECURITY: Token decoding and expiry logic
  static decodeToken(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map((char) => {
        const hex = char.charCodeAt(0).toString(16).padStart(2, '0');
        return `%${hex}`;
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Token decode error:', error);
      return null;
    }
  }

  static getTokenPayload<T = any>(): T | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    return this.decodeToken(token) as T | null;
  }

  static getTokenExpiry(): number | null {
    const payload = this.getTokenPayload<{ exp?: number }>();
    if (!payload || typeof payload.exp !== 'number') {
      return null;
    }
    return payload.exp * 1000;
  }

  static isTokenExpired(): boolean {
    const expiresAt = this.getTokenExpiry();
    if (!expiresAt) {
      return false;
    }
    return Date.now() >= expiresAt;
  }

  // Login
  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      console.log('🔐 Attempting login with:', credentials);
      console.log('🔐 API_BASE_URL:', API_BASE_URL);
      console.log('🔐 Axios baseURL:', axios.defaults.baseURL);

      const response: AxiosResponse<LoginResponse> = await axios.post(
        `${AUTH_ENDPOINTS.LOGIN}`,
        credentials
      );

      console.log('🔐 Full response object:', response);
      console.log('🔐 Response data:', response.data);
      console.log('🔐 Response data keys:', Object.keys(response.data || {}));
      console.log('🔐 Response data stringified:', JSON.stringify(response.data, null, 2));

      const { success, token, user, message, rolePermissions, userPermissions } = response.data;

      console.log('🔍 Destructured response:', {
        success,
        token: token ? 'EXISTS' : 'NULL',
        user: user ? 'EXISTS' : 'NULL',
        message,
        responseDataHasData: 'data' in response.data,
        rolePermissions: rolePermissions?.length || 0,
        userPermissions: userPermissions?.length || 0
      });

      if (success) {
        // Try different ways to get token and user
        let actualToken = token;
        let actualUser = user;

        // If token/user not in root level, try from data object
        if (!actualToken && (response.data as any).data) {
          actualToken = (response.data as any).data.token;
          actualUser = (response.data as any).data.user || (response.data as any).data;
        }

        console.log('🔐 Final extraction attempt:', {
          actualToken: actualToken ? `${actualToken.substring(0, 20)}...` : 'NULL',
          actualUser: actualUser ? actualUser.Username || 'EXISTS' : 'NULL'
        });

        if (actualToken && actualUser) {
          console.log('🔐 Calling setAuthData...');
          this.setAuthData(actualToken, actualUser, rolePermissions, userPermissions);
          console.log('✅ Auth data set successfully');

          // Wait for localStorage to be ready
          await new Promise(resolve => setTimeout(resolve, 300));

          console.log('✅ Login completed successfully - ready to redirect');
        } else {
          console.error('❌ Missing token or user in response:', { actualToken, actualUser });
        }
      } else {
        console.log('❌ Login failed:', message);
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ Login error:', error);

      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        message: 'Lỗi kết nối tới server'
      };
    }
  }

  // Logout
  static async logout(): Promise<ApiResponse> {
    try {
      const token = this.getToken();

      if (token) {
        await axios.post(
          `${AUTH_ENDPOINTS.LOGOUT}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }

      this.clearAuthData();

      return {
        success: true,
        message: 'Đăng xuất thành công'
      };
    } catch (error: any) {
      console.error('❌ Logout error:', error);

      // Clear local data even if server request fails
      this.clearAuthData();

      return {
        success: true,
        message: 'Đăng xuất thành công'
      };
    }
  }

  // Get user profile
  static async getProfile(): Promise<ApiResponse<User>> {
    try {
      const token = this.getToken();

      if (!token) {
        return {
          success: false,
          message: 'Không có token xác thực'
        };
      }

      const response: AxiosResponse<ApiResponse<User>> = await axios.get(
        `${AUTH_ENDPOINTS.ME}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Get profile error:', error);

      if (error.response?.status === 401) {
        this.clearAuthData();
        return {
          success: false,
          message: 'Phiên đăng nhập đã hết hạn'
        };
      }

      return {
        success: false,
        message: 'Lỗi khi lấy thông tin người dùng'
      };
    }
  }

  // Change password
  static async changePassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<ApiResponse> {
    try {
      const token = this.getToken();

      if (!token) {
        return {
          success: false,
          message: 'Không có token xác thực'
        };
      }

      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${AUTH_ENDPOINTS.CHANGE_PASSWORD}`,
        {
          currentPassword,
          newPassword,
          confirmPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('❌ Change password error:', error);

      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        message: 'Lỗi khi đổi mật khẩu'
      };
    }
  }

  // Verify token (simplified - local only, no server call)
  static async verifyToken(token?: string): Promise<boolean> {
    const tokenToVerify = token || this.getToken();
    const user = this.getUser();

    // Simple local verification - just check if token and user exist
    return !!(tokenToVerify && user && tokenToVerify.length > 10);
  }

  // Refresh token (simplified - local only for now)  
  static async refreshToken(): Promise<boolean> {
    // For now, just check if we have valid token and user
    const token = this.getToken();
    const user = this.getUser();

    return !!(token && user);
  }

  // Setup axios interceptors for automatic token handling
  static setupInterceptors(): void {
    // Set axios defaults baseURL
    axios.defaults.baseURL = API_BASE_URL;
    console.log('🌐 Axios baseURL set to:', axios.defaults.baseURL);

    // Request interceptor to add token
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token && token.length > 10) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Content-Type'] = 'application/json';
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - simplified to only handle 401s
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('🔐 Unauthorized - clearing auth and redirecting to login');
          this.clearAuthData();

          // Only redirect if not already on login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Get authorization header
  static getAuthHeader(): { Authorization: string } | {} {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Check if user has specific role
  static hasRole(requiredRole: string): boolean {
    const user = this.getUser();
    return user?.RoleName === requiredRole;
  }

  // Check if user has any of the specified roles
  static hasAnyRole(roles: string[]): boolean {
    const user = this.getUser();
    return user ? roles.includes(user.RoleName || '') : false;
  }

  // Debug method to check auth state
  static debugAuthState(): void {
    const token = this.getToken();
    const user = this.getUser();
    const isAuth = this.isAuthenticated();

    console.log('🔍 AUTH STATE DEBUG:', {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'NULL',
      hasUser: !!user,
      username: user?.Username || 'NULL',
      isAuthenticated: isAuth,
      localStorage: {
        token: !!localStorage.getItem(TOKEN_KEY),
        user: !!localStorage.getItem(USER_KEY)
      },
      sessionStorage: {
        token: !!sessionStorage.getItem(TOKEN_KEY),
        user: !!sessionStorage.getItem(USER_KEY)
      }
    });
  }
} 